<template>
  <v-dialog :value="dialog" @input="$emit('update:dialog', $event)" fullscreen hide-overlay transition="dialog-bottom-transition">
    <v-card dark tile class="exercise-dialog-bg">
      <v-toolbar flat dark color="transparent" class="pt-2">
        <v-btn icon dark @click="$emit('update:dialog', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-bold">{{ dialogTitle }}</v-toolbar-title>
      </v-toolbar>

      <v-card-text class="pa-4 pt-0">
        <div v-if="mode === 'swap' && exerciseToSwap" class="context-card mb-4">
          <div class="context-label">Tausche aus:</div>
          <v-list-item class="px-0">
            <v-list-item-avatar tile size="50" class="rounded-lg">
              <v-img :src="getImageForExercise(exerciseToSwap)"></v-img>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold white--text">{{ exerciseToSwap.title }}</v-list-item-title>
              <v-list-item-subtitle>
                <v-chip
                  v-for="tag in getMuscleGroups(exerciseToSwap)"
                  :key="tag"
                  x-small
                  dark
                  color="rgba(255, 255, 255, 0.2)"
                  class="muscle-chip mr-1">
                  {{ tag }}
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </div>

        <v-text-field
          v-model="exerciseSearch"
          :label="searchLabel"
          filled
          rounded
          dense
          clearable
          solo
          flat
          hide-details
          background-color="rgba(255, 255, 255, 0.1)"
          prepend-inner-icon="mdi-magnify"
          class="mb-4"></v-text-field>

        <div class="list-container">
          <v-row no-gutters dense>
            <v-col cols="12" class="pa-1" v-for="(item, index) in filteredExercises" :key="item._id">
              <v-lazy transition="fade-transition" :options="{ threshold: 0.5 }">
                <v-card
                  @click="selectExercise(item)"
                  class="exercise-list-card animated-item"
                  :style="{ '--animation-delay': `${index * 0.05}s` }"
                  outlined>
                  <v-list-item>
                    <v-list-item-avatar tile size="60" class="rounded-lg">
                      <v-img :src="getImageForExercise(item)" :lazy-src="getPlaceholderImage()"></v-img>
                    </v-list-item-avatar>
                    <v-list-item-content class="pl-3">
                      <v-list-item-title class="font-weight-bold white--text">{{ item.title }}</v-list-item-title>
                      <v-list-item-subtitle>
                        <v-chip
                          v-for="tag in getMuscleGroups(item)"
                          :key="tag"
                          x-small
                          dark
                          color="rgba(255, 255, 255, 0.15)"
                          class="muscle-chip mr-1 mt-1">
                          {{ tag }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </v-card>
              </v-lazy>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapGetters } from "vuex"

export default {
  name: "ExerciseSelectionDialog",
  props: {
    dialog: { type: Boolean, required: true },
    // MODIFIZIERT: 'mode' steuert, ob wir hinzufügen oder tauschen.
    mode: { type: String, default: "swap", validator: (val) => ["swap", "add"].includes(val) },
    // MODIFIZIERT: Bei 'add' brauchen wir den Index, wo eingefügt wird.
    insertionIndex: { type: Number, default: 0 },
    // 'exerciseToSwap' wird jetzt nur noch im 'swap'-Modus verwendet.
    exerciseToSwap: { type: Object, default: null },
  },
  data() {
    return {
      exerciseSearch: "",
    }
  },
  computed: {
    ...mapGetters("deviceStore", ["exerciseList"]),

    // NEU: Der Titel passt sich dem Modus an.
    dialogTitle() {
      return this.mode === "swap" ? "Übung austauschen" : "Übung hinzufügen"
    },

    // NEU: Das Label für die Suche passt sich ebenfalls an.
    searchLabel() {
      return this.mode === "swap" ? "Finde passenden Ersatz..." : "Suche eine Übung zum Hinzufügen..."
    },

    filteredExercises() {
      if (!this.exerciseList) return []
      if (!this.exerciseSearch) return this.exerciseList
      const searchLower = this.exerciseSearch.toLowerCase()

      // Schliesst die aktuell ausgewählte Übung aus der Liste aus (nur im Tausch-Modus)
      const baseList =
        this.mode === "swap" && this.exerciseToSwap ? this.exerciseList.filter((ex) => ex._id !== this.exerciseToSwap._id) : this.exerciseList

      return baseList.filter((exercise) => {
        if (exercise.deleted || exercise.deviceCategory === "(ALT) MedX Kraftgeräte") {
          return false
        }
        return exercise.title.toLowerCase().includes(searchLower)
      })
    },
  },
  methods: {
    // MODIFIZIERT: Sendet jetzt ein spezifisches Event je nach Modus.
    selectExercise(exercise) {
      if (this.mode === "swap") {
        this.$emit("swap-selected", {
          newExercise: exercise,
          exerciseIndex: this.exerciseToSwap.originalIndex, // Wir brauchen den Index der zu tauschenden Übung
        })
      } else {
        // mode === 'add'
        this.$emit("add-selected", {
          newExercise: exercise,
          atIndex: this.insertionIndex,
        })
      }
      this.$emit("update:dialog", false)
    },
    getMuscleGroups(exercise) {
      if (!exercise || !exercise.tags) return []
      return exercise.tags.map((t) => t.name).slice(0, 4)
    },
    getImageForExercise(exercise) {
      if (exercise.picture) return `/img/uebungen/${exercise.picture.replace(/\.[^/.]+$/, "")}.webp`
      if (exercise.manual && exercise.manual[0]?.picture) {
        return `/img/uebungen/${exercise.manual[0].picture.replace(/\.[^/.]+$/, "")}.webp`
      }
      return this.getPlaceholderImage()
    },
    getPlaceholderImage() {
      return "/img/einmalig/imageUploadPlaceholder.jpg"
    },
  },
}
</script>

<style scoped>
.exercise-dialog-bg {
  background: #1e1e1e;
}

.list-container {
  height: calc(100vh - 220px); /* Höhe für Toolbar, Suche und Kontext-Header abziehen */
  overflow-y: auto;
}

/* NEU: Styling für den Kontext-Header */
.context-card {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 8px 16px;
}
.context-label {
  font-size: 0.75rem;
  color: #a0a0a0;
  margin-bottom: -4px;
}

.exercise-list-card {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  transition: background-color 0.2s ease-in-out;
}
.exercise-list-card:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.muscle-chip {
  backdrop-filter: blur(5px);
  font-size: 0.65rem !important;
  padding: 0 6px;
}

/* Coole Fade-In Animation für die Items */
.animated-item {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.5s ease forwards;
  animation-delay: var(--animation-delay);
}

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
